(() => {
  if (customElements.get('gf-product-photo-viewer')) return;
  const touchLayout = window.matchMedia('(max-width: 1024px)');
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  customElements.define('gf-product-photo-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.data = JSON.parse(this.querySelector('[data-gf-photo-data]').textContent);
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.gf-photo-stage');
      this.image = this.querySelector('.gf-photo-image');
      this.loading = this.querySelector('.gf-photo-loading');
      this.error = this.querySelector('.gf-photo-error');
      this.count = this.querySelector('[data-gf-photo-count]');
      this.scaleOutput = this.querySelector('[data-gf-photo-scale]');
      this.previous = this.querySelector('[data-gf-photo-previous]');
      this.next = this.querySelector('[data-gf-photo-next]');
      this.zoomIn = this.querySelector('[data-gf-photo-in]');
      this.zoomOut = this.querySelector('[data-gf-photo-out]');
      this.resetButton = this.querySelector('[data-gf-photo-reset]');
      this.pointers = new Map();
      this.openHandler = this.onOpenClick.bind(this);
      document.addEventListener('click', this.openHandler, true);
      this.querySelector('[data-gf-photo-close]').addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-gf-photo-retry]').addEventListener('click', () => this.showImage(this.index));
      this.previous.addEventListener('click', () => this.navigate(-1));
      this.next.addEventListener('click', () => this.navigate(1));
      this.zoomIn.addEventListener('click', () => this.setZoom(this.scale + .5));
      this.zoomOut.addEventListener('click', () => this.setZoom(this.scale - .5));
      this.resetButton.addEventListener('click', () => this.setZoom(1));
      this.image.addEventListener('load', () => this.onImageLoad());
      this.image.addEventListener('error', () => {
        this.loading.hidden = true;
        this.error.hidden = false;
        this.image.hidden = true;
      });
      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          if (this.scale > 1) {
            this.x += event.key === 'ArrowRight' ? -60 : 60;
            this.paint();
          } else this.navigate(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === '+' || event.key === '=') {
          event.preventDefault(); this.setZoom(this.scale + .5);
        } else if (event.key === '-') {
          event.preventDefault(); this.setZoom(this.scale - .5);
        } else if (event.key === 'Home') {
          event.preventDefault(); this.setZoom(1);
        } else if (this.scale > 1 && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault(); this.y += event.key === 'ArrowDown' ? -60 : 60; this.paint();
        }
      });
      this.stage.addEventListener('pointerdown', event => this.pointerDown(event));
      this.stage.addEventListener('pointermove', event => this.pointerMove(event));
      this.stage.addEventListener('pointerup', event => this.pointerEnd(event, false));
      this.stage.addEventListener('pointercancel', event => this.pointerEnd(event, true));
      this.stage.addEventListener('dblclick', event => {
        event.preventDefault();
        if (Date.now() - (this.lastTouchTime || 0) > 600) this.toggleZoom(event.clientX, event.clientY);
      });
      this.layoutHandler = () => {
        if (!this.dialog.open) return;
        if (!touchLayout.matches) this.dialog.close();
        else this.fit();
      };
      this.resizeObserver = new ResizeObserver(() => {
        if (this.dialog.open && this.image.complete && this.image.naturalWidth) this.fit();
      });
      this.resizeObserver.observe(this.stage);
      touchLayout.addEventListener('change', this.layoutHandler);
      this.setupGallery();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.openHandler, true);
      touchLayout.removeEventListener('change', this.layoutHandler);
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
    }
    get gallery() { return document.getElementById(this.dataset.galleryId); }
    get items() {
      return [...(this.gallery?.querySelectorAll('.product__media-list > [data-media-id]') || [])].filter(item => item.clientWidth > 0);
    }
    setupGallery() {
      const gallery = this.gallery;
      if (!gallery) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const controls = viewer.querySelector('.slider-buttons');
      const refresh = () => {
        if (this.galleryFrame) return;
        this.galleryFrame = requestAnimationFrame(() => {
          this.galleryFrame = null;
          if (innerWidth >= 750) return;
          const list = viewer.querySelector('.product__media-list');
          const items = this.items;
          if (!list || !items.length) return;
          const left = list.getBoundingClientRect().left;
          const active = items.reduce((best, item) => Math.abs(item.getBoundingClientRect().left - left) < Math.abs(best.getBoundingClientRect().left - left) ? item : best, items[0]);
          controls?.toggleAttribute('data-gf-single-media', items.length < 2);
          const current = controls?.querySelector('.slider-counter--current');
          const total = controls?.querySelector('.slider-counter--total');
          if (current) current.textContent = items.indexOf(active) + 1;
          if (total) total.textContent = items.length;
        });
      };
      viewer.addEventListener('slideChanged', refresh);
      viewer.querySelector('.product__media-list')?.addEventListener('scroll', refresh, {passive:true});
      new MutationObserver(refresh).observe(viewer.querySelector('.product__media-list'), {childList:true, attributes:true, subtree:true, attributeFilter:['class']});
      refresh();
    }
    onOpenClick(event) {
      if (!touchLayout.matches) return;
      const button = event.target.closest('modal-opener.product__modal-opener--image .product__media-toggle');
      if (!button || button.closest('media-gallery') !== this.gallery) return;
      const allItems = [...this.gallery.querySelectorAll('.product__media-list > [data-media-id]')];
      const ordered = allItems.filter(item => !item.classList.contains('product__media-item--variant') || item.classList.contains('is-active') || item === allItems.find(entry => entry.classList.contains('product__media-item--variant'))).map(item => this.data.images.find(image => String(image.id) === item.dataset.mediaId.split('-').pop())).filter(Boolean);
      if (!ordered.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.images = ordered;
      this.opener = button;
      this.startId = String(button.dataset.mediaId);
      this.scrollPosition = window.scrollY;
      this.bodyStyle = {position:document.body.style.position, top:document.body.style.top, width:document.body.style.width};
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      window.pauseAllMedia?.();
      this.dialog.showModal();
      this.showImage(Math.max(0, this.images.findIndex(image => String(image.id) === this.startId)));
    }
    showImage(index) {
      const focused = document.activeElement;
      this.index = clamp(index, 0, this.images.length - 1);
      this.current = this.images[this.index];
      this.scale = 1;
      this.x = this.y = 0;
      this.pointers.clear();
      this.lastTap = null;
      this.loading.hidden = false;
      this.error.hidden = true;
      this.image.hidden = true;
      this.count.textContent = `${this.index + 1} / ${this.images.length}`;
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.images.length - 1;
      this.previous.hidden = this.next.hidden = this.images.length < 2;
      if (focused === this.next && this.next.disabled && !this.previous.disabled) this.previous.focus({preventScroll:true});
      if (focused === this.previous && this.previous.disabled && !this.next.disabled) this.next.focus({preventScroll:true});
      this.image.alt = this.current.alt || this.data.title;
      this.image.width = this.current.width;
      this.image.height = this.current.height;
      this.image.src = this.current.url;
      this.scaleOutput.textContent = '100%';
      this.zoomIn.disabled = this.zoomOut.disabled = this.resetButton.disabled = true;
      if (this.image.complete && this.image.naturalWidth) this.onImageLoad();
    }
    onImageLoad() {
      if (!this.dialog.open) return;
      this.loading.hidden = true;
      this.error.hidden = true;
      this.image.hidden = false;
      this.fit();
    }
    fit() {
      const bounds = this.stage.getBoundingClientRect();
      const naturalWidth = this.image.naturalWidth;
      const naturalHeight = this.image.naturalHeight;
      if (!naturalWidth || !bounds.width || !bounds.height) return;
      const ratio = Math.min((bounds.width - 24) / naturalWidth, (bounds.height - 24) / naturalHeight, 1);
      this.baseWidth = naturalWidth * ratio;
      this.baseHeight = naturalHeight * ratio;
      this.image.style.width = `${this.baseWidth}px`;
      this.image.style.height = `${this.baseHeight}px`;
      this.maxScale = Math.max(1, Math.min(4, 1 / ratio));
      this.scale = 1;
      this.x = this.y = 0;
      this.paint();
    }
    setZoom(value, clientX, clientY) {
      if (!this.baseWidth || this.image.hidden) return;
      const next = clamp(value, 1, this.maxScale);
      const rect = this.stage.getBoundingClientRect();
      const anchorX = (clientX ?? rect.left + rect.width / 2) - rect.left - rect.width / 2;
      const anchorY = (clientY ?? rect.top + rect.height / 2) - rect.top - rect.height / 2;
      this.x = anchorX - (anchorX - this.x) * next / this.scale;
      this.y = anchorY - (anchorY - this.y) * next / this.scale;
      this.scale = next;
      this.paint();
    }
    paint() {
      if (!this.baseWidth) return;
      const rect = this.stage.getBoundingClientRect();
      const maxX = Math.max(0, (this.baseWidth * this.scale - rect.width) / 2);
      const maxY = Math.max(0, (this.baseHeight * this.scale - rect.height) / 2);
      this.x = clamp(this.x, -maxX, maxX);
      this.y = clamp(this.y, -maxY, maxY);
      this.image.style.transform = `translate(-50%, -50%) translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
      this.stage.toggleAttribute('data-zoomed', this.scale > 1.01);
      const focused = document.activeElement;
      this.zoomOut.disabled = this.scale <= 1.01;
      this.resetButton.disabled = false;
      this.zoomIn.disabled = this.scale >= this.maxScale - .01;
      if ((focused === this.zoomOut && this.zoomOut.disabled) || (focused === this.zoomIn && this.zoomIn.disabled)) this.resetButton.focus({preventScroll:true});
      const percentage = `${Math.round(this.scale * 100)}%`;
      if (this.scaleOutput.textContent !== percentage) this.scaleOutput.textContent = percentage;
    }
    toggleZoom(x, y) { this.setZoom(this.scale > 1.01 ? 1 : Math.min(2.5, this.maxScale), x, y); }
    navigate(direction) {
      const target = this.index + direction;
      if (target >= 0 && target < this.images.length) this.showImage(target);
    }
    pointerDown(event) {
      if (event.pointerType === 'touch') this.lastTouchTime = Date.now();
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, {x:event.clientX, y:event.clientY});
      if (this.pointers.size === 1) {
        this.gesture = {x:event.clientX, y:event.clientY, startX:this.x, startY:this.y, scale:this.scale, time:Date.now(), moved:false, pinched:false};
      } else if (this.pointers.size === 2) {
        this.gesture.pinched = true;
        const [a,b] = [...this.pointers.values()];
        this.pinch = {distance:Math.hypot(a.x-b.x,a.y-b.y), scale:this.scale};
      }
      this.stage.toggleAttribute('data-dragging', this.scale > 1);
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, {x:event.clientX, y:event.clientY});
      if (this.pointers.size === 2 && this.pinch?.distance) {
        const [a,b] = [...this.pointers.values()];
        this.setZoom(this.pinch.scale * Math.hypot(a.x-b.x,a.y-b.y) / this.pinch.distance, (a.x+b.x)/2, (a.y+b.y)/2);
      } else if (this.pointers.size === 1 && this.gesture) {
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        if (Math.hypot(dx,dy) > 8) this.gesture.moved = true;
        if (this.scale > 1 && !this.gesture.pinched) {
          this.x = this.gesture.startX + dx;
          this.y = this.gesture.startY + dy;
          this.paint();
        }
      }
    }
    pointerEnd(event, cancelled) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      this.stage.removeAttribute('data-dragging');
      const g = this.gesture;
      if (!g || cancelled || g.pinched) {
        if (!this.pointers.size) this.gesture = null;
        return;
      }
      const dx = event.clientX - g.x;
      const dy = event.clientY - g.y;
      if (this.scale <= 1.01 && Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        this.navigate(dx < 0 ? 1 : -1);
      } else if (!g.moved && event.pointerType === 'touch' && Date.now() - g.time < 300) {
        const now = Date.now();
        if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(event.clientX-this.lastTap.x,event.clientY-this.lastTap.y) < 35) {
          this.toggleZoom(event.clientX,event.clientY);
          this.lastTap = null;
        } else this.lastTap = {time:now,x:event.clientX,y:event.clientY};
      }
      this.gesture = null;
    }
    onClose() {
      this.pointers.clear();
      Object.assign(document.body.style, this.bodyStyle);
      window.scrollTo({top:this.scrollPosition, behavior:'instant'});
      if (this.current && String(this.current.id) !== this.startId && touchLayout.matches) {
        const item = [...this.gallery.querySelectorAll('.product__media-list > [data-media-id]')].find(item => item.dataset.mediaId.endsWith(`-${this.current.id}`));
        item?.querySelectorAll('img[data-gf-gallery-image]').forEach(image => {
          image.loading = 'eager';
          image.srcset = image.dataset.gfGallerySrcset;
          image.src = image.dataset.gfGallerySrc;
          image.removeAttribute('data-gf-gallery-image');
          image.removeAttribute('data-gf-gallery-srcset');
          image.removeAttribute('data-gf-gallery-src');
        });
        if (item && this.gallery?.setActiveMedia) this.gallery.setActiveMedia(item.dataset.mediaId, false);
        const button = item?.querySelector('.product__media-toggle');
        button?.focus({preventScroll:true});
      } else this.opener?.focus({preventScroll:true});
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
  });
})();
